import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  Clock,
  User,
  Calendar,
  CheckCircle,
  XCircle,
  Edit3,
  Trash2,
  X,
  Save,
} from 'lucide-react';
import { adminAPI } from '../../services/api';

interface AdminReminder {
  id: number;
  user_id: number;
  user_name?: string;
  user_email?: string;
  type: string;
  title: string;
  message?: string;
  frequency: string;
  time: string;
  enabled: boolean;
  last_sent?: string;
  next_send?: string;
  created_at: string;
}

interface EditForm {
  title: string;
  message: string;
  type: string;
  frequency: string;
  time: string;
  enabled: boolean;
}

export const AdminReminders: React.FC = () => {
  const [reminders, setReminders] = useState<AdminReminder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [selectedReminder, setSelectedReminder] = useState<AdminReminder | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<EditForm>({
    title: '',
    message: '',
    type: 'medication',
    frequency: 'daily',
    time: '09:00',
    enabled: true,
  });
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    loadReminders();
  }, []);

  const loadReminders = async () => {
    try {
      const response = await adminAPI.getAllReminders();
      if (response.success) {
        setReminders(response.data.reminders || []);
      }
    } catch (error) {
      console.error('Error loading reminders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewReminder = (reminder: AdminReminder) => {
    setSelectedReminder(reminder);
    setShowModal(true);
    setIsEditing(false);
    setSaveSuccess(false);
    setSaveError('');
    setEditForm({
      title: reminder.title || '',
      message: reminder.message || '',
      type: reminder.type || 'medication',
      frequency: reminder.frequency || 'daily',
      time: reminder.time || '09:00',
      enabled: reminder.enabled,
    });
  };

  const handleSaveReminder = async () => {
    if (!selectedReminder) return;

    setActionLoading('save');
    setSaveError('');
    setSaveSuccess(false);

    try {
      const response = await adminAPI.updateReminder(selectedReminder.id, editForm);

      if (response.success) {
        const updatedReminder = { ...selectedReminder, ...editForm };
        setSelectedReminder(updatedReminder);
        setReminders(reminders.map(r => r.id === selectedReminder.id ? updatedReminder : r));
        setSaveSuccess(true);
        setIsEditing(false);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setSaveError('Error al guardar los cambios');
      }
    } catch (error) {
      console.error('Error saving reminder:', error);
      setSaveError('Error al guardar los cambios');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteReminder = async (reminder: AdminReminder) => {
    if (!window.confirm(`¿Estás seguro de eliminar el recordatorio "${reminder.title}"?`)) {
      return;
    }

    setActionLoading(`delete-${reminder.id}`);
    try {
      const response = await adminAPI.deleteReminder(reminder.id);
      if (response.success) {
        setReminders(reminders.filter(r => r.id !== reminder.id));
        setShowModal(false);
      }
    } catch (error) {
      console.error('Error deleting reminder:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFrequencyLabel = (frequency: string) => {
    const labels: Record<string, string> = {
      once: 'Una vez',
      daily: 'Diario',
      weekly: 'Semanal',
      monthly: 'Mensual',
    };
    return labels[frequency] || frequency;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'medication': return '💊';
      case 'tension': return '❤️';
      case 'headache': return '🤕';
      default: return '🔔';
    }
  };

  const filteredReminders = reminders.filter(r => {
    if (filter === 'active') return r.enabled;
    if (filter === 'inactive') return !r.enabled;
    return true;
  });

  const activeCount = reminders.filter(r => r.enabled).length;
  const inactiveCount = reminders.filter(r => !r.enabled).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-text)]">Recordatorios</h1>
        <p className="text-[var(--color-text-muted)]">Gestión de recordatorios del sistema</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-5"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Bell size={24} className="text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[var(--color-text)]">{reminders.length}</p>
              <p className="text-sm text-[var(--color-text-muted)]">Total recordatorios</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-5"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle size={24} className="text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[var(--color-text)]">{activeCount}</p>
              <p className="text-sm text-[var(--color-text-muted)]">Activos</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card p-5"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-900/30 flex items-center justify-center">
              <XCircle size={24} className="text-gray-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[var(--color-text)]">{inactiveCount}</p>
              <p className="text-sm text-[var(--color-text-muted)]">Inactivos</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {(['all', 'active', 'inactive'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-xl font-medium transition-all ${
              filter === f
                ? 'bg-[var(--color-accent)] text-white'
                : 'bg-[var(--color-surface)] text-[var(--color-text-muted)] hover:bg-[var(--color-bg)]'
            }`}
          >
            {f === 'all' ? 'Todos' : f === 'active' ? 'Activos' : 'Inactivos'}
          </button>
        ))}
      </div>

      {/* Reminders List */}
      <div className="space-y-3">
        {filteredReminders.length === 0 ? (
          <div className="card p-8 text-center">
            <Bell size={48} className="mx-auto text-[var(--color-text-muted)] mb-4 opacity-50" />
            <p className="text-[var(--color-text-muted)]">No hay recordatorios</p>
          </div>
        ) : (
          filteredReminders.map((reminder, index) => (
            <motion.div
              key={reminder.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="card p-4 cursor-pointer hover:border-[var(--color-accent)] transition-colors"
              onClick={() => handleViewReminder(reminder)}
            >
              <div className="flex items-start gap-4">
                <div className="text-2xl">{getTypeIcon(reminder.type)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-[var(--color-text)]">{reminder.title}</h3>
                    {reminder.enabled ? (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                        Activo
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400">
                        Inactivo
                      </span>
                    )}
                  </div>

                  {reminder.message && (
                    <p className="text-sm text-[var(--color-text-muted)] mb-2">{reminder.message}</p>
                  )}

                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-1 text-[var(--color-text-muted)]">
                      <User size={14} />
                      <span>{reminder.user_name || `Usuario #${reminder.user_id}`}</span>
                    </div>
                    <div className="flex items-center gap-1 text-[var(--color-text-muted)]">
                      <Clock size={14} />
                      <span>{reminder.time} - {getFrequencyLabel(reminder.frequency)}</span>
                    </div>
                    {reminder.next_send && (
                      <div className="flex items-center gap-1 text-[var(--color-text-muted)]">
                        <Calendar size={14} />
                        <span>Próximo: {formatDate(reminder.next_send)}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewReminder(reminder);
                      setIsEditing(true);
                    }}
                    className="p-2 rounded-lg hover:bg-[var(--color-bg)] text-[var(--color-accent)]"
                  >
                    <Edit3 size={18} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteReminder(reminder);
                    }}
                    disabled={actionLoading === `delete-${reminder.id}`}
                    className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500"
                  >
                    {actionLoading === `delete-${reminder.id}` ? (
                      <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Trash2 size={18} />
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {showModal && selectedReminder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[var(--color-surface)] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
                <h2 className="text-lg font-semibold text-[var(--color-text)]">
                  {isEditing ? 'Editar Recordatorio' : 'Detalles del Recordatorio'}
                </h2>
                <div className="flex items-center gap-2">
                  {!isEditing && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="p-2 rounded-lg hover:bg-[var(--color-bg)] transition-colors text-[var(--color-accent)]"
                    >
                      <Edit3 size={20} />
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setIsEditing(false);
                    }}
                    className="p-2 rounded-lg hover:bg-[var(--color-bg)] transition-colors"
                  >
                    <X size={20} className="text-[var(--color-text-muted)]" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-4 space-y-4">
                {/* User Info */}
                <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                  <p className="text-xs text-[var(--color-text-muted)] mb-1">Usuario</p>
                  <p className="text-sm font-medium text-[var(--color-text)]">
                    {selectedReminder.user_name || `Usuario #${selectedReminder.user_id}`}
                  </p>
                  {selectedReminder.user_email && (
                    <p className="text-xs text-[var(--color-text-muted)]">{selectedReminder.user_email}</p>
                  )}
                </div>

                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
                        Título
                      </label>
                      <input
                        type="text"
                        value={editForm.title}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        className="input"
                        placeholder="Título del recordatorio"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
                        Mensaje
                      </label>
                      <textarea
                        value={editForm.message}
                        onChange={(e) => setEditForm({ ...editForm, message: e.target.value })}
                        className="input min-h-[80px] resize-none"
                        placeholder="Mensaje opcional"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
                          Tipo
                        </label>
                        <select
                          value={editForm.type}
                          onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                          className="input"
                        >
                          <option value="medication">Medicación</option>
                          <option value="tension">Tensión</option>
                          <option value="headache">Dolor de cabeza</option>
                          <option value="custom">Personalizado</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
                          Frecuencia
                        </label>
                        <select
                          value={editForm.frequency}
                          onChange={(e) => setEditForm({ ...editForm, frequency: e.target.value })}
                          className="input"
                        >
                          <option value="once">Una vez</option>
                          <option value="daily">Diario</option>
                          <option value="weekly">Semanal</option>
                          <option value="monthly">Mensual</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
                          Hora
                        </label>
                        <input
                          type="time"
                          value={editForm.time}
                          onChange={(e) => setEditForm({ ...editForm, time: e.target.value })}
                          className="input"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
                          Estado
                        </label>
                        <select
                          value={editForm.enabled ? 'true' : 'false'}
                          onChange={(e) => setEditForm({ ...editForm, enabled: e.target.value === 'true' })}
                          className="input"
                        >
                          <option value="true">Activo</option>
                          <option value="false">Inactivo</option>
                        </select>
                      </div>
                    </div>

                    {/* Success/Error Messages */}
                    {saveSuccess && (
                      <div className="p-3 rounded-xl bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 text-sm">
                        Cambios guardados correctamente
                      </div>
                    )}
                    {saveError && (
                      <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
                        {saveError}
                      </div>
                    )}

                    {/* Save/Cancel Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveReminder}
                        disabled={actionLoading === 'save'}
                        className="btn btn-primary flex-1"
                      >
                        {actionLoading === 'save' ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Save size={18} />
                            Guardar Cambios
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          setEditForm({
                            title: selectedReminder.title || '',
                            message: selectedReminder.message || '',
                            type: selectedReminder.type || 'medication',
                            frequency: selectedReminder.frequency || 'daily',
                            time: selectedReminder.time || '09:00',
                            enabled: selectedReminder.enabled,
                          });
                        }}
                        className="btn btn-secondary"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Reminder Details */}
                    <div className="space-y-3">
                      <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                        <p className="text-xs text-[var(--color-text-muted)] mb-1">Título</p>
                        <p className="text-sm font-medium text-[var(--color-text)]">{selectedReminder.title}</p>
                      </div>

                      {selectedReminder.message && (
                        <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                          <p className="text-xs text-[var(--color-text-muted)] mb-1">Mensaje</p>
                          <p className="text-sm text-[var(--color-text)]">{selectedReminder.message}</p>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                          <p className="text-xs text-[var(--color-text-muted)] mb-1">Tipo</p>
                          <p className="text-sm font-medium text-[var(--color-text)] flex items-center gap-2">
                            {getTypeIcon(selectedReminder.type)} {selectedReminder.type}
                          </p>
                        </div>
                        <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                          <p className="text-xs text-[var(--color-text-muted)] mb-1">Frecuencia</p>
                          <p className="text-sm font-medium text-[var(--color-text)]">
                            {getFrequencyLabel(selectedReminder.frequency)}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                          <p className="text-xs text-[var(--color-text-muted)] mb-1">Hora</p>
                          <p className="text-sm font-medium text-[var(--color-text)]">{selectedReminder.time}</p>
                        </div>
                        <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                          <p className="text-xs text-[var(--color-text-muted)] mb-1">Estado</p>
                          <p className={`text-sm font-medium ${selectedReminder.enabled ? 'text-green-500' : 'text-gray-500'}`}>
                            {selectedReminder.enabled ? 'Activo' : 'Inactivo'}
                          </p>
                        </div>
                      </div>

                      {selectedReminder.next_send && (
                        <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                          <p className="text-xs text-[var(--color-text-muted)] mb-1">Próximo envío</p>
                          <p className="text-sm font-medium text-[var(--color-text)]">
                            {formatDate(selectedReminder.next_send)}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => setIsEditing(true)}
                        className="btn btn-primary flex-1"
                      >
                        <Edit3 size={18} />
                        Editar
                      </button>
                      <button
                        onClick={() => handleDeleteReminder(selectedReminder)}
                        disabled={actionLoading === `delete-${selectedReminder.id}`}
                        className="btn bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400"
                      >
                        {actionLoading === `delete-${selectedReminder.id}` ? (
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Trash2 size={18} />
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
